import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SOFTWARE_PROJECT } from '@atlassian/jira-common-constants/src/project-types.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import {
	performGetRequest,
	performPostRequest,
	performPutRequest,
} from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { useFlagsService } from '@atlassian/jira-flags';
import { useIntl } from '@atlassian/jira-intl';
import { useProjectSidebarNavigation } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/common/project-sidebar-navigation-resource/index.tsx';
import getXsrfToken from '@atlassian/jira-platform-xsrf-token/src/index.tsx';
import {
	type UIAnalyticsEvent,
	fireTrackAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import { getAccessibleAvatarName } from '@atlassian/jira-project-details/src/view/avatar-picker-dialog/accessible-avatar-names/index.tsx';
import {
	toErrorId,
	toErrorMessage,
} from '@atlassian/jira-project-settings-apps-common/src/types.tsx';
import { famaCatchAndLogFetchError } from '@atlassian/jira-project-settings-apps-common/src/util/catch-and-log-fetch-error.tsx';
import { PACKAGE_NAME } from '../../common/constants.tsx';
import type { AvatarDetails, CropProperties } from '../../common/types.tsx';
import messages from './messages.tsx';

type ServerAvatar = {
	id: string;
	isSystemAvatar: boolean;
	isSelected: boolean;
	urls: {
		[key: string]: string;
	};
	fileName?: string;
};
type ProjectAvatarsResponse = {
	system: ServerAvatar[];
	custom: ServerAvatar[];
	hasError?: boolean;
};
export type PredefinedAvatar = {
	dataURI: string;
	name: string;
	id: number;
};

export const transformServerAvatarToServiceAvatar = (avatar: ServerAvatar): AvatarDetails => {
	const avatarId: number = parseInt(avatar.id, 10);
	return {
		id: avatarId,
		avatarUrl: `/secure/viewavatar?size=xxxlarge@2x&avatarId=${avatarId}&avatarType=project`,
		fileName: avatar?.fileName,
	};
};

export const requestPredefinedAvatars = (projectId: number): Promise<ProjectAvatarsResponse> =>
	famaCatchAndLogFetchError(
		performGetRequest(`/rest/api/2/universal_avatar/type/project/owner/${projectId}`),
		PACKAGE_NAME,
		toErrorId('getPredefinedAvatars'),
		toErrorMessage('Failed to fetch avatars for project details'),
	).catch(() => ({
		hasError: true,
		system: [],
		custom: [],
	}));

export const updateProjectIcon = (projectId: number, avatarId: number) =>
	famaCatchAndLogFetchError(
		performPutRequest(`/rest/api/2/project/${String(projectId)}`, {
			body: JSON.stringify({ avatarId }),
		}),
		'navigation-app',
		toErrorId('updateProjectIcon'),
		toErrorMessage('Failed to update project Icon'),
	);

export const uploadCustomerIcon = (projectId: number, file: File, crop: CropProperties) => {
	const uploadUrl =
		`/rest/api/2/universal_avatar/type/project/owner/${projectId}` +
		// we are getting XSRF token from cookies directly because it is not being in the store and can be outdated
		`?atl_token=${getXsrfToken() || ''}` +
		`&x=${Math.floor(crop.x)}` +
		`&y=${Math.floor(crop.y)}` +
		`&size=${Math.floor(crop.size)}`;
	return famaCatchAndLogFetchError(
		performPostRequest(uploadUrl, {
			body: file,
			headers: {},
		}),
		PACKAGE_NAME,
		toErrorId('uploadProjectAvatar'),
		toErrorMessage('Failed to upload an avatar for project details'),
	);
};

export const useAvatars = (projectId: number, selectedAvatarUrl: string) => {
	const [predefinedSystemAvatars, setPredefinedSystemAvatars] = useState<PredefinedAvatar[]>([]);
	const [predefinedCustomAvatars, setPredefinedCustomAvatars] = useState<PredefinedAvatar[]>([]);
	const [latestCustomAvatar, setLatestCustomAvatar] = useState<PredefinedAvatar | undefined>(
		undefined,
	);
	const [fetchingError, setFetchingError] = useState<Error | undefined>(undefined);
	const isFetched = useRef<boolean>(false);
	const { refresh } = useProjectSidebarNavigation();
	const { showFlag } = useFlagsService();
	const intl = useIntl();
	const { formatMessage } = intl;

	useEffect(() => {
		if (isFetched.current) {
			return;
		}
		requestPredefinedAvatars(projectId)
			.then((response) => {
				// need to load xxxlarge icons in AvatarModal
				setPredefinedSystemAvatars(
					response.system.map((avatar) => {
						const transformedAvatar = transformServerAvatarToServiceAvatar(avatar);
						return {
							dataURI: transformedAvatar.avatarUrl,
							name: getAccessibleAvatarName(transformedAvatar)(intl),
							id: transformedAvatar.id,
						};
					}),
				);
				setPredefinedCustomAvatars(
					response.custom.map((avatar) => {
						const transformedAvatar = transformServerAvatarToServiceAvatar(avatar);
						return {
							dataURI: transformedAvatar.avatarUrl,
							name: getAccessibleAvatarName(transformedAvatar)(intl),
							id: transformedAvatar.id,
						};
					}),
				);
				if (response.custom.length) {
					const lastCustomAvatar = response.custom[response.custom.length - 1];
					const transformedAvatar = transformServerAvatarToServiceAvatar(lastCustomAvatar);
					setLatestCustomAvatar({
						dataURI: transformedAvatar.avatarUrl,
						name: getAccessibleAvatarName(transformedAvatar)(intl),
						id: transformedAvatar.id,
					});
				}
				if (response.hasError) {
					setFetchingError(new Error('There is an error in fetching fetching avatar'));
				}
			})
			.catch((e) => {
				setFetchingError(e);
			});
		isFetched.current = true;
	}, [isFetched, projectId, intl]);

	const allAvatars = useMemo(
		() => [...predefinedSystemAvatars, ...predefinedCustomAvatars],
		[predefinedSystemAvatars, predefinedCustomAvatars],
	);

	const selectedAvatar = useMemo(() => {
		const selectedAvatarId = parseInt(
			selectedAvatarUrl.substring(selectedAvatarUrl.lastIndexOf('/') + 1),
			10,
		);
		return allAvatars?.find((avatar) => avatar.id === selectedAvatarId);
	}, [allAvatars, selectedAvatarUrl]);

	const onError = useCallback(() => {
		showFlag({
			key: 'update-project-icon-error',
			type: 'error',
			title: formatMessage(messages.updateError),
			messageId:
				'navigation-apps-sidebar-inline-config-project-header.services.use-avatars.show-flag.error',
			messageType: 'transactional',
		});
	}, [showFlag, formatMessage]);

	const updateSelectedAvatar = useCallback(
		(avatarUrl: string, analyticsEvent?: UIAnalyticsEvent) => {
			if (selectedAvatar && avatarUrl === selectedAvatar.dataURI) {
				return;
			}
			const jiraAvatar = allAvatars?.find((avatar) => avatar.dataURI === avatarUrl);
			if (jiraAvatar) {
				updateProjectIcon(projectId, jiraAvatar.id)
					.then(() => {
						analyticsEvent &&
							fireTrackAnalytics(analyticsEvent, 'project updated', {
								projectType: SOFTWARE_PROJECT,
								updatedItems: '[projectIcon]',
							});
						refresh();
					})
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					.catch((error: any) => {
						onError();
						fireErrorAnalytics({
							error,
							meta: {
								id: 'updateProjectIcon',
								packageName: 'jiraNavigationAppsSidebarInlineConfigProjectHeader',
								teamName: 'jlove-makkuro',
							},
						});
					});
			}
		},
		[selectedAvatar, allAvatars, projectId, refresh, onError],
	);

	const uploadCustomerAvatar = useCallback(
		(file: File, crop: CropProperties, analyticsEvent: UIAnalyticsEvent) => {
			if (file && crop) {
				uploadCustomerIcon(projectId, file, crop)
					.then((data) => updateProjectIcon(projectId, data.id))
					.then(() => {
						fireTrackAnalytics(analyticsEvent, 'project updated', {
							projectType: SOFTWARE_PROJECT,
							updatedItems: '[projectIcon]',
						});
						refresh();
					}) // eslint-disable-next-line @typescript-eslint/no-explicit-any
					.catch((error: any) => {
						onError();
						fireErrorAnalytics({
							error,
							meta: {
								id: 'uploadCustomerIcon',
								packageName: 'jiraNavigationAppsSidebarInlineConfigProjectHeader',
								teamName: 'jlove-makkuro',
							},
						});
					});
			}
		},
		[projectId, onError, refresh],
	);

	return {
		selectedAvatar,
		systemAvatars: predefinedSystemAvatars,
		customAvatars: predefinedCustomAvatars,
		fetchingError,
		updateSelectedAvatar,
		uploadCustomerAvatar,
		uploadCustomerIcon,
		allAvatars,
		isFetched,
		latestCustomAvatar,
		setLatestCustomAvatar,
	};
};
